// Step 6 — Stripe SetupIntent + Payment Element (US-028, US-029).
//
// Pixel-faithful frame from `prototypes_and_docs/ls-onboarding-v2.jsx:874-1106`
// (order summary, name field, country select, CTA). The raw card / exp / cvc
// inputs were replaced by Stripe's `<PaymentElement />`: raw PANs never touch
// our API. Flow:
//
//   1. On step entry, `POST /billing/setup-intent` mints a SetupIntent.
//   2. `<Elements>` mounts with its clientSecret + `<PaymentElement />`.
//   3. Submit → `stripe.confirmSetup(redirect:'if_required')` →
//      `POST /billing/start-trial` (server validates the intent + creates
//      the trialing subscription) → only then `onSubmit(result)` runs the
//      existing completion path. A trial failure keeps the user on step 6.

import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronLeft, Lock } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';

import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import {
  useStartTrial,
  useBillingPrices,
  useUpdatePaymentMethod,
  type StartTrialResponse,
} from '@barel/api-client/react';
import { useLocale, useT } from '@barel/i18n';
import { Banner, Button, Card, Field, Input, OnboardingStep, Select, toast } from '@barel/ui';

import { formatDate } from '@/lib/format';
import { getStripe, publishableKey } from '@/lib/stripe';
import {
  findBillingPrice,
  formatBillingAmount,
  formatBillingPrice,
} from '@/features/billing/prices';

import { paymentSchema, type PaymentFormValues, type OnboardingPlan } from '../lib/schemas';

interface PaymentProps {
  plan: OnboardingPlan;
  submitting?: boolean;
  onBack: () => void;
  /** Called ONLY after `POST /billing/start-trial` succeeded (US-029). */
  onSubmit: (result: StartTrialResponse) => void | Promise<void>;
}

const COUNTRIES = ['FR', 'BE', 'CH', 'LU', 'CA', 'GB', 'DE', 'ES', 'IT', 'US'] as const;

export function Payment({ plan, submitting, onBack, onSubmit }: PaymentProps) {
  const t = useT('onboarding');
  const locale = useLocale();
  const priceQuery = useBillingPrices();
  const planPrice = findBillingPrice(priceQuery.data?.prices ?? [], plan, 'monthly');

  const trialEndDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d;
  }, []);

  const setupIntent = useUpdatePaymentMethod();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupIntentId, setSetupIntentId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const pkMissing = !publishableKey();

  // Mint the SetupIntent once on step entry.
  useEffect(() => {
    if (pkMissing) return;
    setupIntent.mutate(undefined, {
      onSuccess: (res) => {
        setClientSecret(res.clientSecret);
        setSetupIntentId(res.setupIntentId);
      },
      onError: () => toast.error({ title: t('step6.errors.payment_failed') }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ready = !pkMissing && !!clientSecret && !!setupIntentId && !!planPrice;
  const submitting$ = !!submitting || confirming;

  const formattedPlanPrice = planPrice ? formatBillingPrice(planPrice, locale) : '—';
  const formattedDueToday = planPrice ? formatBillingAmount(0, planPrice.currency, locale) : '—';
  const formattedTrialEnd = formatDate(trialEndDate, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <OnboardingStep
      width="md"
      eyebrow={t('step6.eyebrow')}
      title={t('step6.title')}
      description={
        <span>
          {t('step6.subtitle')} <strong>{t('step6.subtitleStrong')}</strong>
        </span>
      }
      primary={
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={submitting$}
          disabled={!ready}
          form="onboarding-payment-form"
          data-testid="onboarding-step6-submit"
          iconLeft={<Lock aria-hidden style={{ width: 14, height: 14 }} />}
        >
          {submitting$ ? t('step6.submitting') : t('step6.cta')}
        </Button>
      }
      secondary={
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          disabled={submitting$}
          iconLeft={<ChevronLeft aria-hidden style={{ width: 14, height: 14 }} />}
          data-testid="onboarding-step6-back"
        >
          {t('wizard.back')}
        </Button>
      }
    >
      {/* Order summary */}
      <Card padding="md" radius="lg" data-region="onboarding-order-summary">
        <div className="flex items-start justify-between">
          <div className="flex flex-col">
            <span className="text-sm font-bold text-primary">
              {t('step6.summary.planLine', { plan: t(`step5.plans.${plan}.name`) })}
            </span>
            <span className="text-2xs text-secondary">{t(`step5.plans.${plan}.desc`)}</span>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-base font-bold text-tertiary line-through">
              {formattedPlanPrice}
            </span>
            <span className="text-2xs text-tertiary">{t('step5.perMonth')}</span>
          </div>
        </div>
        <div className="my-3 h-px bg-bg-elevated" />
        <div className="flex items-center justify-between">
          <span className="text-xs text-secondary">{t('step6.summary.todayLine')}</span>
          <span className="text-lg font-bold text-success">{formattedDueToday}</span>
        </div>
        <p className="mt-2 text-2xs text-tertiary leading-relaxed">
          {t('step6.summary.firstChargeLine', {
            date: formattedTrialEnd,
            price: formattedPlanPrice,
          })}
        </p>
      </Card>

      {pkMissing ? (
        <Banner tone="warning" data-testid="onboarding-pmt-pk-missing">
          Stripe publishable key not configured. Set
          <code className="mx-1 rounded bg-bg-elevated px-1">VITE_STRIPE_PUBLISHABLE_KEY</code>
          in your env.
        </Banner>
      ) : priceQuery.isError || (!priceQuery.isLoading && !planPrice) ? (
        <Banner
          tone="warning"
          title={t('step6.errors.price_unavailable')}
          action={
            <Button type="button" variant="ghost" size="sm" onClick={() => priceQuery.refetch()}>
              {t('step5.prices.retry')}
            </Button>
          }
          data-testid="onboarding-payment-price-error"
        >
          {t('step5.prices.errorBody')}
        </Banner>
      ) : !clientSecret || !setupIntentId || priceQuery.isLoading ? (
        <p className="text-sm text-secondary" data-testid="onboarding-pmt-loading">
          {t('step6.loading')}
        </p>
      ) : (
        <Elements stripe={getStripe()} options={{ clientSecret }}>
          <PaymentForm
            plan={plan}
            setupIntentId={setupIntentId}
            disabled={submitting$}
            onBusyChange={setConfirming}
            onSubmit={onSubmit}
          />
        </Elements>
      )}

      <p className="text-2xs text-tertiary text-right">{t('step6.poweredBy')}</p>
    </OnboardingStep>
  );
}

interface PaymentFormProps {
  plan: OnboardingPlan;
  setupIntentId: string;
  disabled: boolean;
  onBusyChange: (busy: boolean) => void;
  onSubmit: (result: StartTrialResponse) => void | Promise<void>;
}

function PaymentForm({ plan, setupIntentId, disabled, onBusyChange, onSubmit }: PaymentFormProps) {
  const t = useT('onboarding');
  const stripe = useStripe();
  const elements = useElements();
  const startTrial = useStartTrial();
  // Card setup and trial creation are separate network operations. Preserve a
  // successful SetupIntent when the API step fails so retrying never attempts
  // to confirm an already-succeeded intent again.
  const [confirmedSetupIntentId, setConfirmedSetupIntentId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentSchema),
    mode: 'onBlur',
    defaultValues: { name: '', country: 'FR' },
  });

  const country = watch('country');

  const errorMessage = (key: keyof PaymentFormValues): string | undefined => {
    const message = errors[key]?.message;
    if (!message) return undefined;
    const KNOWN_CODES = ['name_required', 'country_invalid'] as const;
    if ((KNOWN_CODES as readonly string[]).includes(message)) {
      return t(`step6.errors.${message}`);
    }
    return t('step6.errors.generic');
  };

  const submit: SubmitHandler<PaymentFormValues> = async (values) => {
    if (!confirmedSetupIntentId && (!stripe || !elements)) return;
    onBusyChange(true);
    try {
      let confirmedId = confirmedSetupIntentId;
      if (!confirmedId) {
        if (!stripe || !elements) return;
        const result = await stripe.confirmSetup({
          elements,
          redirect: 'if_required',
          confirmParams: { return_url: window.location.href },
        });
        if (result.error) {
          toast.error({
            title: t('step6.errors.payment_failed'),
            description: result.error.message,
          });
          return;
        }
        confirmedId =
          typeof result.setupIntent?.id === 'string' ? result.setupIntent.id : setupIntentId;
        setConfirmedSetupIntentId(confirmedId);
      }
      // Server-side: validates the intent, attaches the PM, creates the
      // trialing subscription. Only a success unlocks completion.
      const trial = await startTrial.mutateAsync({
        plan,
        setupIntentId: confirmedId,
        country: values.country,
      });
      await onSubmit(trial);
    } catch {
      // start-trial failed — stay on step 6 (US-028); completion never ran.
      toast.error({ title: t('step6.errors.trial_failed') });
    } finally {
      onBusyChange(false);
    }
  };

  return (
    <form
      id="onboarding-payment-form"
      onSubmit={handleSubmit(submit)}
      className="flex flex-col gap-3"
      noValidate
    >
      <Field invalid={!!errors.name} required>
        <Field.Label>{t('step6.fields.name.label')}</Field.Label>
        <Field.Control>
          <Input
            {...register('name')}
            placeholder={t('step6.fields.name.placeholder')}
            autoComplete="cc-name"
            data-testid="onboarding-pmt-name"
            disabled={disabled}
          />
        </Field.Control>
        {errors.name ? <Field.Error>{errorMessage('name')}</Field.Error> : null}
      </Field>

      <div data-testid="onboarding-pmt-element">
        <PaymentElement />
      </div>

      <Field invalid={!!errors.country}>
        <Field.Label>{t('step6.fields.country.label')}</Field.Label>
        <Field.Control>
          <Select
            value={country}
            onValueChange={(next: string) => setValue('country', next, { shouldValidate: true })}
          >
            <Select.Trigger aria-label={t('step6.fields.country.label')} size="md">
              <Select.Value />
            </Select.Trigger>
            <Select.Portal>
              <Select.Content>
                {COUNTRIES.map((code) => (
                  <Select.Item key={code} value={code}>
                    {t(`step6.fields.country.options.${code}`)}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Portal>
          </Select>
        </Field.Control>
      </Field>
    </form>
  );
}
